'use client'

import { useEffect, useMemo, useState } from 'react'

import {
  dismissNotification,
  clearAllNotifications,
  markNotificationRead,
  markNotificationUnread,
  markAllNotificationsRead,
  getNotifications,
  subscribeNotifications,
} from '@lib/notifications'
import type { Notification } from '@lib/notifications'

export type { Notification, NotificationType, NotificationAction } from '@lib/notifications'

interface UseNotificationsReturn {
  notifications: Notification[]
  unreadCount: number
  dismiss: (id: string) => Promise<void>
  clearAll: () => Promise<void>
  markRead: (id: string) => Promise<void>
  markUnread: (id: string) => Promise<void>
  markAllRead: () => Promise<void>
}

export function useNotifications(): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<Notification[]>([])

  useEffect(() => {
    let mounted = true

    const load = async () => {
      const data = await getNotifications()
      if (mounted) {
        setNotifications(data)
      }
    }

    load()

    const unsubscribe = subscribeNotifications({
      onNotification: (n) => {
        if (!mounted) return
        setNotifications((prev) => {
          const filtered = prev.filter((p) => p.id !== n.id)

          return [n, ...filtered]
        })
      },
      onDismiss: (id) => {
        if (!mounted) return
        setNotifications((prev) => prev.filter((p) => p.id !== id))
      },
      onClear: () => {
        if (!mounted) return
        setNotifications([])
      },
      onMarkRead: (id, readAt) => {
        if (!mounted) return
        setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, readAt } : n)))
      },
      onMarkUnread: (id) => {
        if (!mounted) return
        setNotifications((prev) =>
          prev.map((n) => {
            if (n.id !== id) return n
            const { readAt: _, ...rest } = n

            return rest
          }),
        )
      },
      onMarkAllRead: (readAt) => {
        if (!mounted) return
        setNotifications((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt })))
      },
    })

    return () => {
      mounted = false
      unsubscribe()
    }
  }, [])

  const unreadCount = useMemo(() => notifications.filter((n) => !n.readAt).length, [notifications])

  return {
    notifications,
    unreadCount,
    dismiss: dismissNotification,
    clearAll: clearAllNotifications,
    markRead: markNotificationRead,
    markUnread: markNotificationUnread,
    markAllRead: markAllNotificationsRead,
  }
}
